// src/components/assessment/AssessmentAgent.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bot, ChevronRight, CheckCircle2, Send, Lock } from 'lucide-react'; 
import ReactMarkdown from 'react-markdown';
import { SCORING_METRICS } from '../../data/assessment-config';

// --- FIREBASE CALLABLE IMPORTS ---
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase-config'; 

const AssessmentAgent = ({ stage, onNext, onSkip, onContextData, context, activeQuestion, sessionId }) => {
    const [displayedMessage, setDisplayedMessage] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    
    // --- CONVERSATION STATE ---
    const [chatHistory, setChatHistory] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [conversationComplete, setConversationComplete] = useState(false); 
    const [hasConnectionError, setHasConnectionError] = useState(false); // <-- NEW: Escape Hatch State

    // --- REFS FOR STABILITY ---
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const hasInitializedChat = useRef(false); // Prevents double-fire
    const isMounted = useRef(true); 

    // --- CLEANUP ---
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);
    
    // --- AUTO-FOCUS LOGIC ---
    useEffect(() => {
        if (stage === 'clinical_active' && !isTyping && !conversationComplete) {
            const timer = setTimeout(() => {
                if (inputRef.current) inputRef.current.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [stage, isTyping, chatHistory, conversationComplete]);

    // --- HELPER: RESOLVE FRIENDLY NAME ---
    const getFriendlyName = useCallback((rawKey) => {
        if (!rawKey) return "Performance";
        
        const FAILSAFE_LABELS = {
            sleep_perf: "Sleep",
            injury_mgmt: "Pain Management",
            nutrition: "Nutrition",
            focus: "Focus",
            resilience: "Resilience",
            motivation: "Motivation",
            confidence: "Confidence",
            discipline: "Discipline",
            awareness: "Self-Awareness",
            adaptability: "Adaptability"
        };

        let label = SCORING_METRICS?.[rawKey]?.label 
            || FAILSAFE_LABELS[rawKey]
            || rawKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        if (label.includes('(')) label = label.split('(')[0].trim();
        
        return label;
    }, []);

    // --- THE GATE'S LINE ---
    // The panel only appears at the clinical gate and during the conversation.
    // The questions, the self-rating and the focus test run without it (the
    // engine carries its own hints), and nothing types itself out any more.
    const getGuidanceMessage = useCallback((currentStage) => {
        if (currentStage !== 'clinical_gate') return "";
        const metricLabel = getFriendlyName(context?.targetMetric);
        return context?.mode === 'clinical'
            ? `Your **${metricLabel}** answers stand out. A few questions about what is behind them will shape your plan. Ready?`
            : `**${metricLabel}** is the area with the most room. A few questions about it will shape your plan. Ready?`;
    }, [context, getFriendlyName]);

    useEffect(() => {
        setDisplayedMessage(stage === 'clinical_active' || stage === 'complete' ? "" : getGuidanceMessage(stage));
    }, [stage, getGuidanceMessage]);

    // --- DEEP LISTENING LOGIC ---
    // UPDATED: Accepts isHidden parameter to support invisible priming
    const handleVertexAISend = useCallback(async (text, isHidden = false) => {
        if (!text.trim()) return;

        // 1. Add User Message to UI (ONLY IF NOT HIDDEN)
        if (!isHidden) {
            const newMsg = { sender: 'user', text: text };
            setChatHistory(prev => [...prev, newMsg]);
            setUserInput("");
        }
        
        setIsTyping(true);

        if (!sessionId) {
            if (!isHidden) {
                setChatHistory(prev => [...prev, { sender: 'ai', text: "Error: Session ID missing." }]);
            }
            setIsTyping(false);
            setHasConnectionError(true); // <-- Trigger Escape Hatch
            return;
        }

        try {
            const vertexAiGateway = httpsCallable(functions, 'vertexAiGateway');
            
            // Resolve Friendly Name for Context to prevent "sleep_perf" leaks in system prompt
            const friendlyTarget = getFriendlyName(context?.targetMetric);

            const result = await vertexAiGateway({
                message: text,
                sessionId: sessionId,
                context: {
                    stage: stage,
                    targetMetric: friendlyTarget, 
                    radarScores: onContextData?.radarScores,
                    baselineAnswers: onContextData?.baselineAnswers
                }
            });

            if (!isMounted.current) return;
            
            setHasConnectionError(false); // <-- Clear error on success

            // Add AI Response (Always visible)
            setChatHistory(prev => [...prev, { sender: 'ai', text: result.data.text }]);

            if (result.data.status === 'complete') {
                setConversationComplete(true);
            }

        } catch (error) {
            console.error("Deep Listening Error:", error);
            const errorMessage = error.message.includes("internal") 
                ? "I'm having trouble connecting right now. Let's try again." 
                : "Connection error. Please check your internet.";
            
            if (isMounted.current) {
                if (!isHidden) {
                    setChatHistory(prev => [...prev, { sender: 'ai', text: errorMessage }]);
                }
                setHasConnectionError(true); // <-- Trigger Escape Hatch
            }
        } finally {
            if (isMounted.current) setIsTyping(false);
        }
    }, [sessionId, stage, context, onContextData, getFriendlyName]);

    // --- INITIALIZATION ---
    useEffect(() => {
        if (stage === 'clinical_active' && !hasInitializedChat.current) {
            hasInitializedChat.current = true;
            
            const target = context?.targetMetric;
            const friendlyLabel = getFriendlyName(target);
            const score = onContextData?.radarScores?.[target] || 50;
            
            // Rich Context Prompt
            const primingMessage = `I scored ${score}/100 on ${friendlyLabel}. This is my lowest area. Please guide me to find the root cause. Start by asking about a specific habit that might be affecting this.`;
            
            // SEND AS HIDDEN MESSAGE (Second arg = true)
            handleVertexAISend(primingMessage, true); 
        }
    }, [stage, context?.targetMetric, onContextData, handleVertexAISend, getFriendlyName]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, isTyping, conversationComplete]);

    // --- COMPACT MODE (all non-clinical stages) ---
    // The clinical chat is disabled for many orgs, so outside 'clinical_active' we render a
    // slim guidance strip + action buttons instead of the full agent panel.
    if (stage !== 'clinical_active') {
        if (stage !== 'clinical_gate') return null;
        return (
            <div className="flex flex-col bg-slate-50 border-l lg:border-l-0 border-slate-200">
                {displayedMessage && (
                    <div className="px-4 py-2.5 flex items-start gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-teal-700 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                            <Bot size={13} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0 text-slate-700 text-sm leading-snug [&_p]:inline [&_p]:mr-1">
                            <ReactMarkdown>{displayedMessage}</ReactMarkdown>
                        </div>
                    </div>
                )}

                {stage === 'clinical_gate' && (
                    <div className="px-4 pb-3 flex flex-col gap-2">
                        <button onClick={onNext} className="w-full bg-teal-600 text-white px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 shadow-sm text-sm">
                            <CheckCircle2 size={16} /> Yes, let's talk.
                        </button>
                        <button onClick={onSkip} className="w-full bg-white border border-slate-200 text-slate-500 px-4 py-3 rounded-xl font-bold text-sm">
                            Skip for now
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // --- FULL MODE (clinical chat) — unchanged ---
    return (
        <div className="h-full flex flex-col bg-slate-50 border-l lg:border-l-0 border-slate-200">
            {/* Agent Header */}
            {/* CHANGED: p-3 to px-3 py-1.5, gap-3 to gap-2 */}
            <div className="px-3 py-1.5 bg-teal-700 text-white shadow-md flex items-center gap-2 shrink-0">
                {/* CHANGED: p-2 to p-1.5 */}
                <div className="p-1.5 bg-white/10 rounded-full border border-white/10">
                    {/* CHANGED: size={20} to size={16} */}
                    <Bot size={16} className="text-white" />
                </div>
                <div>
                    <h3 className="font-bold text-sm tracking-wide leading-tight">Assessment Assistant</h3>
                    <div className="flex items-center gap-1.5 opacity-90 mt-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${conversationComplete ? 'bg-slate-400' : (hasConnectionError ? 'bg-orange-400' : 'bg-green-400 animate-pulse')}`}></span>
                        {/* CHANGED: text-[10px] to text-[9px] to fit the tighter height */}
                        <span className="text-[9px] uppercase font-medium">{conversationComplete ? "Analysis Complete" : (hasConnectionError ? "Connection Degraded" : "Data Privacy Mode")}</span>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 p-5 overflow-y-auto flex flex-col gap-2">
                {/* GUIDANCE STAGES */}
                {stage !== 'clinical_active' && (
                    <div className="flex gap-3 animate-in slide-in-from-left-2">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center shrink-0 border border-teal-200 mt-1">
                            <Bot size={14} className="text-teal-700" />
                        </div>
                        <div className="bg-white p-4 rounded-2xl rounded-tl-none border border-slate-200 shadow-sm text-slate-700 text-sm leading-relaxed">
                            <ReactMarkdown>{displayedMessage}</ReactMarkdown>
                            {isTyping && <span className="inline-block w-1.5 h-4 bg-teal-500 ml-1 animate-pulse align-middle"></span>}
                        </div>
                    </div>
                )}

                {/* CLINICAL CHAT STAGE */}
                {stage === 'clinical_active' && (
                    <>
                        {chatHistory.map((msg, i) => (
                            <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] p-3 rounded-xl text-sm ${msg.sender === 'user' ? 'bg-teal-600 text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-none shadow-sm'}`}>
                                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-slate-200 p-3 rounded-2xl rounded-tl-none flex items-center gap-1 w-fit shadow-sm">
                                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></div>
                                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce delay-75"></div>
                                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce delay-150"></div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </>
                )}

                {/* FOOTER ACTIONS */}
                {stage === 'radar' && !isTyping && (
                    // ADDED 'hidden lg:flex' so it hides on mobile and prevents the duplicate
                    <div className="mt-auto pt-4 hidden lg:flex justify-end">
                        <button onClick={onNext} className="bg-teal-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-teal-700 flex items-center gap-2 shadow-lg text-sm">
                            Confirm Shape <ChevronRight size={16} />
                        </button>
                    </div>
                )}

                {stage === 'clinical_gate' && !isTyping && (
                    <div className="mt-auto pt-4 flex flex-col gap-2">
                        <button onClick={onNext} className="w-full bg-teal-600 text-white px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 shadow-sm text-sm">
                            <CheckCircle2 size={16} /> Yes, let's talk.
                        </button>
                        <button onClick={onSkip} className="w-full bg-white border border-slate-200 text-slate-500 px-4 py-3 rounded-xl font-bold text-sm">
                            Skip for now
                        </button>
                    </div>
                )}

                {/* FINISH BUTTON: Shows if explicitly complete, over 4 turns, OR if a connection error occurs */}
                {stage === 'clinical_active' && (conversationComplete || chatHistory.length > 4 || hasConnectionError) && (
                     <div className="mt-auto pt-4 flex flex-col gap-2 justify-center animate-in slide-in-from-bottom duration-500">
                        {hasConnectionError && !conversationComplete && (
                            <p className="text-xs text-center text-slate-500 font-bold mb-1 px-4">
                                Network issues detected. You can save your progress now to avoid losing data.
                            </p>
                        )}
                        <button 
                            onClick={onNext} 
                            className="w-full bg-slate-900 text-white px-6 py-4 rounded-xl font-bold hover:bg-black transition-all shadow-xl flex items-center justify-center gap-2"
                        >
                            <CheckCircle2 size={20} className={hasConnectionError && !conversationComplete ? "text-orange-400" : "text-green-400"} />
                            {hasConnectionError && !conversationComplete ? "Save Progress & Exit" : "Finish and Log Results"}
                        </button>
                    </div>
                )}
            </div>

            {/* INPUT FIELD: Hidden when conversation is complete */}
            {stage === 'clinical_active' && !conversationComplete && (
                <div className="p-3 bg-white border-t border-slate-200 animate-in slide-in-from-bottom duration-300">
                    <form 
                        onSubmit={(e) => { e.preventDefault(); handleVertexAISend(userInput); }}
                        className="flex items-center gap-2 bg-slate-50 p-2 rounded-xl border border-slate-200 focus-within:border-teal-500 transition-colors"
                    >
                        <input 
                            ref={inputRef}
                            className="flex-1 bg-transparent border-none outline-none text-sm px-2 text-slate-700 placeholder:text-slate-400"
                            placeholder={isTyping ? "Analyzing..." : "Describe what's happening..."}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            disabled={isTyping}
                        />
                        <button 
                            type="submit" 
                            disabled={!userInput.trim() || isTyping} 
                            className={`p-2 rounded-lg transition-all ${(!userInput.trim() || isTyping) ? 'bg-slate-200 text-slate-400' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                        >
                            <Send size={16} />
                        </button>
                    </form>
                </div>
            )}

            {/* LOCKED STATE: Shows when complete */}
            {stage === 'clinical_active' && conversationComplete && (
                <div className="p-4 bg-green-50 border-t border-green-100 text-center animate-in fade-in">
                    <p className="text-xs text-green-700 font-bold uppercase tracking-wider flex items-center justify-center gap-2">
                        <Lock size={12} /> Analysis Complete
                    </p>
                </div>
            )}
        </div>
    );
};

export default AssessmentAgent;